import { BOM_UTF8 } from '@imp/shared/csv';
import { csvJournal, equilibre, nomFichierExport } from '@imp/shared/export-comptable';
import {
  attenteReglements,
  csvAttente,
  csvReglements,
  journalReglements,
  MODES_REGLEMENT,
} from '@imp/shared/journal-reglements';
import { messageErreur } from '@imp/shared/message-erreur';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, FileSpreadsheet } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';


import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatEuro } from '@/modules/catalogue/api';
import {
  getReglagesExport,
  listFacturesEnAttente,
  listReglementsPeriode,
  marquerReglementsExportes,
  pointerReglement,
  telechargerCsv,
} from '@/modules/factures/export-comptable-api';

const jourIso = (d: Date): string =>
  `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const dateFr = (iso: string): string => new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR');

/**
 * Journal des règlements : ce que les clients ont payé, à pointer comme le
 * compte bancaire, et ce qu'ils doivent encore. Deux exports pour le
 * comptable (les écritures), deux pour soi (les listes), tous en CSV.
 */
export function ReglementsPage() {
  const queryClient = useQueryClient();
  const aujourdhui = new Date();
  const [debut, setDebut] = useState(`${String(aujourdhui.getFullYear())}-01-01`);
  const [fin, setFin] = useState(jourIso(aujourdhui));
  const [seulementNouveaux, setSeulementNouveaux] = useState(true);
  const [statut, setStatut] = useState<string | null>(null);

  const reglagesQ = useQuery({ queryKey: ['export-comptable-reglages'], queryFn: getReglagesExport });
  const reglementsQ = useQuery({
    queryKey: ['reglements-periode', debut, fin],
    queryFn: () => listReglementsPeriode(debut, fin),
    enabled: Boolean(debut && fin && debut <= fin),
  });
  const attenteQ = useQuery({ queryKey: ['factures-en-attente'], queryFn: listFacturesEnAttente });

  const reglements = reglementsQ.data ?? [];
  const pointes = reglements.filter((r) => r.pointe);
  const totalRecu = reglements.reduce((s, r) => s + (r.factureKind === 'avoir' ? -Math.abs(r.montant) : r.montant), 0);
  const totalPointe = pointes.reduce((s, r) => s + (r.factureKind === 'avoir' ? -Math.abs(r.montant) : r.montant), 0);
  const aExporter = seulementNouveaux ? reglements.filter((r) => r.exportLot === null) : reglements;
  const attente = attenteReglements(attenteQ.data ?? [], jourIso(aujourdhui));

  const invalider = () => {
    void queryClient.invalidateQueries({ queryKey: ['reglements-periode'] });
  };
  const pointerMut = useMutation({
    mutationFn: ({ id, pointe }: { id: string; pointe: boolean }) => pointerReglement(id, pointe),
    onSuccess: invalider,
    onError: (e: Error) => {
      setStatut(`Pointage impossible : ${e.message}`);
    },
  });

  // Pour le comptable : les écritures (banque au débit, client au crédit).
  const exportComptaMut = useMutation({
    mutationFn: async () => {
      const reglages = reglagesQ.data;
      if (!reglages) throw new Error('réglages non chargés');
      if (aExporter.length === 0) throw new Error('aucun règlement à exporter');
      const journal = journalReglements(aExporter, reglages);
      const eq = equilibre(journal.ecritures);
      if (eq.ecart !== 0) throw new Error(`écritures déséquilibrées (écart ${formatEuro(eq.ecart)})`);
      const nom = nomFichierExport(jourIso(new Date()), debut, fin).replace(/^export/, 'reglements');
      telechargerCsv(nom, csvJournal(journal.ecritures), reglages.encodage);
      await marquerReglementsExportes(
        aExporter.map((r) => r.id),
        nom,
      );
      return { nom, nb: aExporter.length };
    },
    onSuccess: ({ nom, nb }) => {
      setStatut(`Fichier ${nom} téléchargé : ${String(nb)} règlement(s), marqué(s) comme exporté(s).`);
      invalider();
    },
    onError: (e: Error) => {
      setStatut(`Export impossible : ${e.message}`);
    },
  });

  // Pour soi : des listes lisibles dans Excel.
  const exporterListe = () => {
    telechargerCsv(
      `reglements-${debut}-${fin}.csv`,
      BOM_UTF8 + csvReglements(reglements, new Set(pointes.map((r) => r.id))),
      'utf-8',
    );
  };
  const exporterAttente = () => {
    telechargerCsv(`en-attente-${jourIso(aujourdhui)}.csv`, BOM_UTF8 + csvAttente(attente.lignes), 'utf-8');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button asChild variant="ghost" size="icon">
          <Link to="/factures" aria-label="Retour">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">Règlements</h1>
          <p className="text-muted-foreground">
            Ce que les clients ont payé — à pointer quand vous le retrouvez sur le relevé — et ce
            qu&apos;ils doivent encore.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/factures/export-comptable">
            <FileSpreadsheet className="h-5 w-5" /> Export comptable
          </Link>
        </Button>
      </div>

      {statut ? (
        <div className="rounded-md border border-border bg-card px-4 py-2 text-sm">{statut}</div>
      ) : null}

      {/* ----- En attente ----- */}
      <Card className={attente.nbEnRetard > 0 ? 'border-2 border-prio-high' : undefined}>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>En attente de règlement</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                Factures émises non soldées, la plus en retard d&apos;abord.
              </p>
            </div>
            <Button type="button" variant="outline" size="sm" disabled={attente.lignes.length === 0} onClick={exporterAttente}>
              <Download className="h-4 w-4" /> Exporter (CSV)
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-md border px-3 py-2">
              <div className="text-xs uppercase text-muted-foreground">Reste à encaisser</div>
              <div className="text-xl font-bold tabular-nums text-money-in">{formatEuro(attente.totalReste)}</div>
              <div className="text-xs text-muted-foreground">{attente.lignes.length} facture(s)</div>
            </div>
            <div className={`rounded-md border px-3 py-2 ${attente.nbEnRetard > 0 ? 'border-prio-high' : ''}`}>
              <div className="text-xs uppercase text-muted-foreground">Dont en retard</div>
              <div className={`text-xl font-bold tabular-nums ${attente.nbEnRetard > 0 ? 'text-prio-high' : ''}`}>
                {formatEuro(attente.totalEnRetard)}
              </div>
              <div className="text-xs text-muted-foreground">{attente.nbEnRetard} facture(s)</div>
            </div>
          </div>
          {attenteQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Lecture des factures…</p>
          ) : attente.lignes.length === 0 ? (
            <p className="text-sm text-muted-foreground">Rien en attente : toutes les factures émises sont soldées.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Facture</th>
                    <th className="px-3 py-2 font-medium">Client</th>
                    <th className="px-3 py-2 font-medium">Échéance</th>
                    <th className="px-3 py-2 text-right font-medium">TTC</th>
                    <th className="px-3 py-2 text-right font-medium">Payé</th>
                    <th className="px-3 py-2 text-right font-medium">Reste dû</th>
                    <th className="px-3 py-2 font-medium">Retard</th>
                  </tr>
                </thead>
                <tbody>
                  {attente.lignes.map((l) => (
                    <tr key={l.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="px-3 py-2 font-mono text-xs">
                        <Link to={`/factures/${l.id}`} className="text-primary hover:underline">
                          {l.numero}
                        </Link>
                      </td>
                      <td className="px-3 py-2">{l.client}</td>
                      <td className="px-3 py-2 tabular-nums">{dateFr(l.dateEcheance)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatEuro(l.montantTtc)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{formatEuro(l.montantPaye)}</td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums text-money-in">{formatEuro(l.reste)}</td>
                      <td className="px-3 py-2">
                        {l.enRetard ? (
                          <span className="rounded bg-money-out-soft px-1.5 py-0.5 text-xs font-semibold text-money-out">
                            {l.joursRetard} j
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">dans {-l.joursRetard} j</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ----- Règlements reçus ----- */}
      <Card>
        <CardHeader>
          <CardTitle>Règlements reçus</CardTitle>
          <p className="text-sm text-muted-foreground">
            Cochez un règlement quand vous l&apos;avez retrouvé sur votre relevé bancaire. Pour le
            comptable, l&apos;export passe la banque au débit et le client au crédit, facture en
            référence.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="debut">Du</Label>
              <Input id="debut" type="date" value={debut} className="w-44" onChange={(e) => { setDebut(e.target.value); }} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fin">Au</Label>
              <Input id="fin" type="date" value={fin} className="w-44" onChange={(e) => { setFin(e.target.value); }} />
            </div>
            <label className="flex h-12 items-center gap-2 text-sm lg:h-11">
              <input
                type="checkbox"
                className="h-5 w-5 accent-primary"
                checked={seulementNouveaux}
                onChange={(e) => {
                  setSeulementNouveaux(e.target.checked);
                }}
              />
              N&apos;exporter que les règlements jamais exportés
            </label>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-md border px-3 py-2">
              <div className="text-xs uppercase text-muted-foreground">Reçu sur la période</div>
              <div className="text-xl font-bold tabular-nums text-money-in">{formatEuro(totalRecu)}</div>
              <div className="text-xs text-muted-foreground">{reglements.length} règlement(s)</div>
            </div>
            <div className="rounded-md border px-3 py-2">
              <div className="text-xs uppercase text-muted-foreground">Pointé</div>
              <div className="text-xl font-bold tabular-nums">{formatEuro(totalPointe)}</div>
              <div className="text-xs text-muted-foreground">{pointes.length} règlement(s)</div>
            </div>
            <div className={`rounded-md border px-3 py-2 ${reglements.length - pointes.length > 0 ? 'border-money-check bg-money-check-soft' : ''}`}>
              <div className="text-xs uppercase text-muted-foreground">Reste à pointer</div>
              <div className="text-xl font-bold tabular-nums text-money-check">{formatEuro(totalRecu - totalPointe)}</div>
              <div className="text-xs text-muted-foreground">{reglements.length - pointes.length} règlement(s)</div>
            </div>
          </div>

          {reglementsQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Lecture des règlements…</p>
          ) : reglementsQ.isError ? (
            <p className="text-sm text-destructive">Erreur : {messageErreur(reglementsQ.error)}</p>
          ) : reglements.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun règlement enregistré sur cette période.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-center font-medium" title="Retrouvé sur le relevé bancaire">Pointé</th>
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Facture</th>
                    <th className="px-3 py-2 font-medium">Client</th>
                    <th className="px-3 py-2 font-medium">Mode</th>
                    <th className="px-3 py-2 font-medium">Référence</th>
                    <th className="px-3 py-2 text-right font-medium">Montant</th>
                    <th className="px-3 py-2 font-medium">Exporté</th>
                  </tr>
                </thead>
                <tbody>
                  {reglements.map((r) => {
                    const montant = r.factureKind === 'avoir' ? -Math.abs(r.montant) : r.montant;
                    return (
                      <tr key={r.id} className={`border-b last:border-0 hover:bg-muted/40 ${r.pointe ? '' : 'bg-money-check-soft/60'}`}>
                        <td className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            className="h-5 w-5 accent-primary"
                            checked={r.pointe}
                            title={r.pointe && r.pointeLe ? `Pointé le ${dateFr(r.pointeLe)}` : 'Pas encore pointé'}
                            onChange={(e) => {
                              pointerMut.mutate({ id: r.id, pointe: e.target.checked });
                            }}
                          />
                        </td>
                        <td className="px-3 py-2 tabular-nums">{dateFr(r.dateReglement)}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.factureNumero}</td>
                        <td className="px-3 py-2">
                          {r.client}
                          {r.compteClient ? <span className="ml-1 font-mono text-xs text-muted-foreground">{r.compteClient}</span> : null}
                        </td>
                        <td className="px-3 py-2">{MODES_REGLEMENT[r.mode] ?? r.mode}</td>
                        <td className="px-3 py-2 text-muted-foreground">{r.reference ?? '—'}</td>
                        <td className={`px-3 py-2 text-right font-semibold tabular-nums ${montant < 0 ? 'text-money-out' : 'text-money-in'}`}>
                          {formatEuro(montant)}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {r.exportLot ? (
                            <span title={r.exportLot}>{r.exportLe ? dateFr(r.exportLe) : 'oui'}</span>
                          ) : (
                            <span className="text-muted-foreground">jamais</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={aExporter.length === 0 || exportComptaMut.isPending || !reglagesQ.data}
              onClick={() => {
                exportComptaMut.mutate();
              }}
            >
              <FileSpreadsheet className="h-5 w-5" /> Export pour le comptable ({aExporter.length})
            </Button>
            <Button type="button" variant="outline" disabled={reglements.length === 0} onClick={exporterListe}>
              <Download className="h-5 w-5" /> Liste des règlements (CSV)
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
